import loginModal from './loginModal.html';
import * as angular from 'angular';
import {AUTH_EVENTS} from './security-module';
import {UserSession} from '../../presentation/routes/contract-features-3/domain';
import {IHttpResponse} from "angular";

export default class security {
    private _currentUser: UserSession = undefined;
    private _modal: any = undefined; // Angular bootstrap modal has no typings
    /*
     isFetchingData and promisesToResolve is used to handle calls to ensureCurrentUser.
     If two calls are done where the second is performed before the first is resolved we need to wait
     with resolving the second until the first is resolved.
     */
    private isFetchingData = false;
    private promisesToResolve: angular.IQResolveReject<UserSession>[] = [];

    constructor(private readonly $http: angular.IHttpService,
                private readonly $uibModal: angular.ui.bootstrap.IModalService,
                private readonly $rootScope: angular.IRootScopeService,
                private readonly $q: angular.IQService,
                private readonly $location: angular.ILocationService,
                private readonly $log: angular.ILogService) {

    }

    resolvePromises(currentUser: UserSession) {
        this.$log.info('Get session, resolving ' + this.promisesToResolve.length);
        this.isFetchingData = false;
        this.promisesToResolve.forEach(function(resolve){
            resolve(currentUser);
        });
        this.promisesToResolve.length = 0;
    }

    ensureCurrentUser(): angular.IPromise<UserSession> {
        return this.$q(function(resolve: angular.IQResolveReject<UserSession>) {
            if (!this._currentUser && !(window as any).noLogin) {
                if (this.isFetchingData) {
                    this.promisesToResolve.push(resolve);
                } else {
                    this.isFetchingData = true;
                    this.promisesToResolve.push(resolve);
                    this.readSession();
                }
            } else {
                this.$log.info('Using existing session');
                resolve(this._currentUser);
            }
        }.bind(this));
    }

    startLogin() {
        if (this._currentUser) {
            const loggedOutUser = this._currentUser;
            this._currentUser = undefined;
            this.$rootScope.$broadcast(AUTH_EVENTS.AUTH_LOGGED_OUT, loggedOutUser);
            this.$rootScope.$broadcast(AUTH_EVENTS.AUTH_CHANGED, this._currentUser, loggedOutUser);
        }
        if (!this._modal) { // When opening the app three AUTH_CHANGED events are fired. This caused three modal to be opened.
            this._modal = this.$uibModal.open({
                template: loginModal,
                controller: 'loginController',
                keyboard: false,
                backdrop: 'static',
                backdropClass: 'loginBackdrop',
                animation: false,
                windowClass: 'loginModal'
            });
        }
    }

    login(username: string, password: string) {
        return this.$http.post('/session/login',
            {
                username,
                password
            })
            .then(function(response: IHttpResponse<UserSession>) {
                const previousUser = this._currentUser;
                const user = response.data;
                this._currentUser = security.transformUser(user);

                this._modal.close();
                this._modal = undefined;
                this.$rootScope.$broadcast(AUTH_EVENTS.AUTH_LOGGED_IN, this._currentUser);
                this.$rootScope.$broadcast(AUTH_EVENTS.AUTH_CHANGED, this._currentUser, previousUser);

                this.resolvePromises(this._currentUser);
                return this._currentUser;
            }.bind(this));

    }

    private static transformUser(user: any): UserSession {
        return new UserSession(user.permissions, user.username, user.name, new Date(user.lastLogin),
            user.featureToggles, user.globalAdmin, user.groupAdminGroupIds, user.changelogStatus,
            user.sensitiveCategories);
    }

    readSession(): angular.IPromise<UserSession> {
        return this.$q(function(resolve: angular.IQResolveReject<UserSession>, reject: angular.IQResolveReject<any>) {
            this.isFetchingData = true;
            this.$http.get('/session').then(function(response: IHttpResponse<UserSession>) {
                    const nextUser = security.transformUser(response.data);
                    this.promisesToResolve.push(resolve);
                    if (!this._currentUser || this._currentUser.username !== nextUser.username) {
                        this.$rootScope.$broadcast(AUTH_EVENTS.AUTH_CHANGED, this._currentUser, nextUser);
                    }
                    this._currentUser = nextUser;
                    this.resolvePromises(nextUser);
                }.bind(this),
                function(response: IHttpResponse<any>) {
                    if (response.status == 404) {
                        this.$log.info('No session starting login');
                        this.promisesToResolve.push(resolve);
                        this.startLogin();
                    } else {
                        reject('response.status ' + response.status);
                    }
                }.bind(this));
        }.bind(this));
    }

    logout() {
        localStorage.removeItem('visibleIcons');
        this.$http.post('/session/logout', {})
            .then(function() {
                const loggedOutUser = this._currentUser;
                this._currentUser = undefined;
                this.$location.url('/');
                this.$rootScope.$broadcast(AUTH_EVENTS.AUTH_LOGGED_OUT, loggedOutUser);
                this.$rootScope.$broadcast(AUTH_EVENTS.AUTH_CHANGED, loggedOutUser, this._currentUser);
            }.bind(this));

        this.startLogin();

    }

    updateToggles(activeToggles: string[]) {
        if (this._currentUser) {
            this._currentUser = this._currentUser.withFeatureToggles(activeToggles);
        }
    }
}

security.$inject = ['$http', '$uibModal', '$rootScope', '$q', '$location', '$log'];
